import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

function App() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => console.error('Failed to copy:', err));
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Hero Animations
    gsap.fromTo('.hero-heading', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.5, ease: 'elastic.out(1, 0.5)' });
    gsap.fromTo('.hero-subheading', { opacity: 0, x: -50 }, { opacity: 1, x: 0, duration: 1.2, delay: 0.5, ease: 'power3.out' });
    gsap.fromTo('.hero-image', { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1, duration: 1.5, delay: 1, ease: 'back.out(1.7)' });

    // Section Animations
    gsap.utils.toArray('.section').forEach((section) => {
      gsap.fromTo(section, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: section, start: 'top 80%' } });
    });

    // Social Buttons Animation
    gsap.fromTo('.social-button', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1, stagger: 0.2, ease: 'bounce.out', delay: 0.5 });

    // Gallery Image Animations
    gsap.utils.toArray('.media-image').forEach((image, index) => {
      gsap.fromTo(image, { opacity: 0, scale: 0.9, rotation: -5 }, {
        opacity: 1, scale: 1, rotation: 0, duration: 1.2, delay: index * 0.2, ease: 'power3.out',
        scrollTrigger: { trigger: image, start: 'top 85%' }
      });
    });

    // FAQ Item Animation
    gsap.utils.toArray('.faq-item').forEach((item) => {
      item.addEventListener('mouseenter', () => gsap.to(item, { scale: 1.02, boxShadow: '0 0 25px rgba(0, 212, 255, 0.7)', duration: 0.3 }));
      item.addEventListener('mouseleave', () => gsap.to(item, { scale: 1, boxShadow: '0 0 15px rgba(0, 212, 255, 0.2)', duration: 0.3 }));
    });
  }, []);

  const contractAddress = "0xVION123...456";

  return (
    <div className="App">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-logo">
          <img src="/logo.jpg" alt="$VION COIN Logo" />
        </div>
        <div className="navbar-ticker">$VION</div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <h1 className="hero-heading">Welcome to $VION COIN Revolution!</h1>
        <h2 className="hero-subheading">Join Vionara in the Cyberpunk Uprising</h2>
        <img src="/gallery1.jpg" alt="Vionara Hero" className="hero-image" />
      </section>

      {/* About Section */}
      <section className="section about-section">
        <div className="container">
          <h2 className="section-title">About $VION COIN</h2>
          <p className="section-content">
            $VION COIN is a revolutionary token launching on the Base chain through Cliza Systems, designed to empower a new generation of crypto enthusiasts, degens, and visionaries. Inspired by the fusion of cyberpunk rebellion and blockchain innovation, $VION COIN embodies the spirit of defiance and creativity in the Web3 era. The name "VION" reflects our dual mission: to provide a vision for a decentralized future and to harness the ionic energy of community-driven change.
            <br /><br />
            At the forefront of $VION COIN stands Vionara, our iconic mascot, a fierce cyberpunk warrior. Vionara is more than a character; she’s a symbol of the strength, resilience, and boldness that define $VION COIN. Forged in the neon-lit crucible of the Base chain, Vionara leads the charge for a token that’s built to disrupt, inspire, and unite.
            <br /><br />
            Launching on May 10, 2025, through Cliza Systems, $VION COIN leverages cutting-edge token launch mechanics to ensure fairness and security. With Cliza’s sniper-proof protocol and locked liquidity, we’re creating a space where the community can thrive without fear of rugs or manipulation. $VION COIN isn’t just a token, it’s a movement. Whether you’re here to trade, meme, or build, $VION COIN offers a platform to be part of something bigger: a revolution that redefines what’s possible on the blockchain.
            <br /><br />
            Join Vionara and the $VION COIN community as we light up the Base chain with the glow of innovation. Stay based, stay bold, and let’s make the future ours.
          </p>
          <div className="social-links">
            <a href="https://x.com/vion_coin" target="_blank" rel="noopener noreferrer" className="social-button">
              <svg className="social-icon" viewBox="0 0 24 24" fill="#FF00FF">
                <path d="M22 4.01c-.81.36-1.68.6-2.59.71.93-.56 1.64-1.45 1.98-2.51-.87.52-1.83.89-2.85 1.09C17.69 2.34 16.47 2 15.15 2c-2.46 0-4.46 2-4.46 4.46 0 .35.04.69.11 1.02C7.94 7.34 5.46 6.04 3.64 4.01c-.38.65-.6 1.41-.6 2.22 0 1.54.78 2.9 1.97 3.7-.73-.02-1.41-.22-2.01-.55v.06c0 2.15 1.53 3.94 3.56 4.35-.37.1-.76.16-1.16.16-.28 0-.56-.03-.83-.08.56 1.75 2.18 3.02 4.1 3.06-1.5 1.18-3.39 1.88-5.44 1.88-.35 0-.7-.02-1.04-.06 1.94 1.24 4.24 1.97 6.72 1.97 8.06 0 12.47-6.68 12.47-12.47 0-.19 0-.38-.01-.57.86-.62 1.6-1.39 2.19-2.27z"/>
              </svg>
            </a>
            <a href="https://t.me/+p0hv5VHLdpE1Zjg5" target="_blank" rel="noopener noreferrer" className="social-button">
              <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" fill="#FF00FF">
                <path d="M248 8C111 8 0 119 0 256S111 504 248 504 496 393 496 256 385 8 248 8zM363 176.7c-3.7 39.2-19.9 134.4-28.1 178.3-3.5 18.6-10.3 24.8-16.9 25.4-14.4 1.3-25.3-9.5-39.3-18.7-21.8-14.3-34.2-23.2-55.3-37.2-24.5-16.1-8.6-25 5.3-39.5 3.7-3.8 67.1-61.5 68.3-66.7 .2-.7 .3-3.1-1.2-4.4s-3.6-.8-5.1-.5q-3.3 .7-104.6 69.1-14.8 10.2-26.9 9.9c-8.9-.2-25.9-5-38.6-9.1-15.5-5-27.9-7.7-26.8-16.3q.8-6.7 18.5-13.7 108.4-47.2 144.6-62.3c68.9-28.6 83.2-33.6 92.5-33.8 2.1 0 6.6 .5 9.6 2.9a10.5 10.5 0 0 1 3.5 6.7A43.8 43.8 0 0 1 363 176.7z"/>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Narrative Section */}
      <section className="section narrative-section">
        <div className="container">
          <h2 className="section-title">The $VION COIN Narrative</h2>
          <p className="section-content">
            In a neon-drenched future where the digital and physical worlds collide, a new force emerges from the shadows of the Base chain, a token born to challenge the status quo and ignite a revolution. $VION COIN isn’t just another cryptocurrency; it’s a beacon of defiance, a spark of innovation, and a rallying cry for those who dare to dream beyond the centralized systems of the past.
            <br /><br />
            At the heart of this uprising stands Vionara, a fierce cyberpunk warrior with a glowing blue mohawk that pulses with the energy of the blockchain. Her short black hair frames a face marked by determination, her intense eyes reflecting the fire of a thousand nodes. Clad in a sleek, armored suit with metallic shoulder pads that shimmer under the neon lights, Vionara is the embodiment of $VION COIN’s spirit, bold, unyielding, and ready to lead the charge. She’s a guardian of the Base chain, a sentinel forged by Cliza Systems to ensure that this token launch is sniper-proof, fair, and unstoppable.
            <br /><br />
            The name "VION" carries a dual meaning: it’s a nod to "vision," reflecting the project’s forward-thinking ethos, and "ion," symbolizing the charged, electric energy that powers this movement. $VION COIN isn’t here to follow trends, it’s here to set them ablaze. Launched through Cliza Systems, a platform renowned for its locked liquidity and innovative token mechanics, $VION COIN promises a community-driven ecosystem where every holder is part of the revolution. Whether you’re a degen trader, a meme coin enthusiast, or a believer in the power of Web3, $VION COIN invites you to join Vionara on this journey to stake your claim in a future where the blockchain is a canvas for rebellion, creativity, and freedom.
            <br /><br />
            On May 10, 2025, the gates will open, and $VION COIN will go live on the Base chain. Vionara stands ready, her mohawk glowing brighter than ever, as she calls out to the crypto warriors of the world: "Stay based, for the revolution is about to take place." The neon lights flicker, the nodes hum with anticipation, and the future beckons. Are you ready to join the VION revolution?
          </p>
          <div className="contract-address-container">
            <p className="section-content">Contract Address:</p>
            <div>
              <input type="text" value={contractAddress} readOnly className="contract-address" />
              <button className="copy-button" onClick={() => copyToClipboard(contractAddress)}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why $VION COIN Section */}
      <section className="section why-section">
        <div className="container">
          <h2 className="section-title">Why $VION COIN?</h2>
          <div className="why-content">
            <div className="why-category">
              <h3 className="why-category-title">Vision & Philosophy</h3>
              <p className="section-content">A Defiant Vision: In a world dominated by centralized control, $VION COIN stands as a beacon of rebellion, offering a decentralized platform where the community holds the power. Inspired by the cyberpunk ethos, we’re here to break the chains of the past and build a future where creativity and freedom thrive.</p>
            </div>
            <div className="why-category">
              <h3 className="why-category-title">Security & Fairness</h3>
              <p className="section-content">Secure and Fair Launch: Partnering with Cliza Systems, $VION COIN ensures a sniper-proof and rug-proof launch on the Base chain. With 95% of the token supply locked in liquidity pools, we’re committed to transparency and fairness, giving every holder an equal chance to join the revolution.</p>
            </div>
            <div className="why-category">
              <h3 className="why-category-title">Community & Innovation</h3>
              <p className="section-content">Community-Driven Innovation: $VION COIN is built by and for the community. Whether you’re a trader, a meme creator, or a Web3 builder, there’s a place for you in our ecosystem. Join us to trade, create, and shape the future of $VION COIN on the Base chain.</p>
            </div>
            <div className="why-category">
              <h3 className="why-category-title">Symbol & Leadership</h3>
              <p className="section-content">A Symbol of Strength: Vionara, our cyberpunk warrior, embodies the resilience and boldness of $VION COIN. With her glowing blue mohawk and futuristic armor, she leads the charge for a token that’s as unyielding as it is innovative. Join her, and let’s make history together.</p>
            </div>
          </div>
          <p className="section-content emphasis">$VION COIN isn’t just about trading, it’s about building a movement. Be part of the revolution that redefines what’s possible on the blockchain.</p>
        </div>
      </section>

      {/* Tokenomics Section */}
      <section className="section tokenomics-section">
        <div className="container">
          <h2 className="section-title">Tokenomics</h2>
          <div className="tokenomics-content">
            <div className="tokenomics-block">
              <h3 className="tokenomics-title">Total Supply</h3>
              <p className="section-content">100% of $VION COIN tokens are allocated at launch, with no hidden minting or inflation mechanisms.</p>
            </div>
            <div className="tokenomics-block">
              <h3 className="tokenomics-title">Liquidity Pool (LP) Lock</h3>
              <p className="section-content">95% of the total supply is locked in liquidity pools by Cliza Systems, ensuring stability and preventing rug pulls. This lock guarantees that the community can trade with confidence, knowing the majority of the supply is secure and accessible for decentralized trading on the Base chain.</p>
            </div>
            <div className="tokenomics-block">
              <h3 className="tokenomics-title">Team Allocation</h3>
              <p className="section-content">5% of the total supply is allocated to the team, vested for 45 days. This vesting period ensures that the team is incentivized to build and grow the $VION COIN ecosystem while aligning their interests with the community’s long-term success.</p>
            </div>
          </div>
          <p className="section-content emphasis">Our tokenomics reflect our commitment to a fair and decentralized launch, empowering the community to drive the $VION COIN revolution. Join us on the Base chain and be part of a token that puts power back in the hands of the people.</p>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="section faqs-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="section-content">
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''}`} onClick={() => toggleFaq(0)}>
              <h3 className="faq-question">What is $VION COIN?</h3>
              <p className="faq-answer">$VION COIN is a community-driven token launching on the Base chain through Cliza Systems. Inspired by cyberpunk rebellion and blockchain innovation, $VION COIN aims to empower crypto enthusiasts, degens, and Web3 builders to join a decentralized revolution. Led by Vionara, our cyberpunk warrior mascot, $VION COIN is a movement to redefine the future of the blockchain.</p>
            </div>
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''}`} onClick={() => toggleFaq(1)}>
              <h3 className="faq-question">When is the $VION COIN launch?</h3>
              <p className="faq-answer">$VION COIN will go live on the Base chain on May 10, 2025. Stay tuned for updates via our Twitter and website as the launch date approaches.</p>
            </div>
            <div className={`faq-item ${activeFaq === 2 ? 'active' : ''}`} onClick={() => toggleFaq(2)}>
              <h3 className="faq-question">What makes $VION COIN different?</h3>
              <p className="faq-answer">$VION COIN combines a powerful cyberpunk narrative with a secure, community-driven token launch. With 95% of the supply locked in liquidity pools by Cliza Systems, we ensure a fair and rug-proof launch. Our focus on community empowerment, paired with the rebellious spirit of Vionara, sets $VION COIN apart as a token that’s built to disrupt and inspire.</p>
            </div>
            <div className={`faq-item ${activeFaq === 3 ? 'active' : ''}`} onClick={() => toggleFaq(3)}>
              <h3 className="faq-question">How is the $VION COIN supply distributed?</h3>
              <p className="faq-answer">The $VION COIN supply is allocated as follows: 95% is locked in liquidity pools by Cliza Systems to ensure trading stability and fairness, while 5% is allocated to the team, vested for 45 days to align their interests with the community’s long-term success.</p>
            </div>
            <div className={`faq-item ${activeFaq === 4 ? 'active' : ''}`} onClick={() => toggleFaq(4)}>
              <h3 className="faq-question">What is Cliza Systems’ role in $VION COIN?</h3>
              <p className="faq-answer">Cliza Systems is the platform facilitating the $VION COIN launch on the Base chain. Known for its sniper-proof and rug-proof token launch mechanics, Cliza Systems ensures a secure and fair launch by locking 95% of the $VION COIN supply in liquidity pools, giving the community confidence to join the revolution.</p>
            </div>
            <div className={`faq-item ${activeFaq === 5 ? 'active' : ''}`} onClick={() => toggleFaq(5)}>
              <h3 className="faq-question">How can I join the $VION COIN community?</h3>
              <p className="faq-answer">Join us on Telegram (link coming soon) to connect with the $VION COIN community, get updates on the launch, and participate in the revolution. Follow us on X (@Vion_coin) for the latest news and announcements.</p>
            </div>
            <div className={`faq-item ${activeFaq === 6 ? 'active' : ''}`} onClick={() => toggleFaq(6)}>
              <h3 className="faq-question">What can I do with $VION COIN?</h3>
              <p className="faq-answer">$VION COIN is a community-driven token designed for trading, meme creation, and participation in the Web3 ecosystem. Whether you’re a degen trader, a meme coin enthusiast, or a Web3 builder, $VION COIN offers a platform to engage, create, and be part of a decentralized movement on the Base chain.</p>
            </div>
            <div className={`faq-item ${activeFaq === 7 ? 'active' : ''}`} onClick={() => toggleFaq(7)}>
              <h3 className="faq-question">Is $VION COIN a safe investment?</h3>
              <p className="faq-answer">While $VION COIN is launched with security in mind, thanks to Cliza Systems’ locked liquidity and sniper-proof mechanics, please note that all cryptocurrencies carry risks. Tokens like $VION COIN can be highly volatile and are not guaranteed investments. Always do your own research (DYOR) and only participate with funds you can afford to lose.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section gallery-section">
        <div className="container">
          <h2 className="section-title">Media Gallery</h2>
          <p className="section-content">Explore the cyberpunk art and graphics of $VION COIN, showcasing Vionara’s world.</p>
          <div className="grid">
            <img src="/hero.jpg" alt="Vionara Art 1" className="media-image" />
            <img src="/gallery2.jpg" alt="Vionara Art 2" className="media-image" />
            <img src="/gallery3.jpg" alt="Vionara Art 3" className="media-image" />
            <img src="/gallery4.jpg" alt="Vionara Art 4" className="media-image" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p className="copyright">© 2025 $VION COIN. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;